import React, { useEffect, useRef, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

import { IChartDataContent, IContent, IContentElement } from '../../api/charts/types';
import { ReactComponent as LockIcon } from '../../assets/icons/lock-icon.svg';
import { ChartType } from '../../types/chartTypes';
import Button from '../Button';
import { CHART_TYPE_TO_SUBTAB_LABELS_MAPPING } from '../charts/constants';
import PurchasePopUp from '../PurchasePopUp';
import { Tab, Tabs } from '../Tabs';

import styles from './TabsContentPanel.module.scss';

export type TabsContentPanelProps<
  TabType extends string,
  TElementExtra extends object = Record<never, never>,
  TContentExtra extends object = Record<never, never>,
> = {
  chartType: ChartType;
  tabs: { key: TabType; label: string }[];
  content: IChartDataContent<TElementExtra, TContentExtra>;
  onTabChange?: (tabKey: TabType) => void;

  /**
   * Domain-agnostic output:
   * - If a section is expanded -> emits the concrete `element`.
   * - If no section is expanded -> emits all `elements` of the current sub-tab.
   * Exactly one of { element, elements } is provided.
   */
  onSelectionChange: (args: {
    tab: TabType;
    subTab: string;
    element?: IContentElement<TElementExtra>;
    elements?: IContentElement<TElementExtra>[];
    parentContent?: IContent<TElementExtra, TContentExtra>;
  }) => void;

  onPurchaseClick?: () => void;
  renderElementExtras?: (args: {
    element: IContentElement<TElementExtra>;
    parentContent: IContent<TElementExtra, TContentExtra>;
    expanded: boolean;
  }) => React.ReactNode;
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.04 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
};

const tabContainerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05 } },
};

const tabItemVariants = {
  hidden: { opacity: 0, y: -6 },
  visible: { opacity: 1, y: 0 },
};

const subTabContainerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.03 } },
};

const subTabItemVariants = {
  hidden: { opacity: 0, x: -8 },
  visible: { opacity: 1, x: 0 },
};

export function TabsContentPanel<
  TabType extends string,
  TElementExtra extends object = Record<never, never>,
  TContentExtra extends object = Record<never, never>,
>(props: TabsContentPanelProps<TabType, TElementExtra, TContentExtra>) {
  const {
    tabs,
    chartType,
    content,
    onTabChange,
    onSelectionChange, // do not use directly in effect deps
    onPurchaseClick,
    renderElementExtras,
  } = props;

  // Keep the latest callback in a ref to avoid re-running effects on each render
  const onSelectionChangeRef = useRef(props.onSelectionChange);
  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  // Parent tab state
  const [activeTab, setActiveTab] = useState<TabType>(tabs[0].key);
  const [activeSubTab, setActiveSubTab] = useState<string>('');
  const [expandedSectionId, setExpandedSectionId] = useState<string | null>(null);

  // When the parent tab changes, pick the first available sub-tab and reset expansion
  useEffect(() => {
    const firstSubKey = Object.keys(content[activeTab] ?? {})[0] ?? '';
    setActiveSubTab(firstSubKey);
    setExpandedSectionId(null);
  }, [activeTab, content]);

  // Emit selection according to current expansion state (element vs elements)
  useEffect(() => {
    const sub = content[activeTab]?.[activeSubTab];
    const elements = sub?.elements || [];

    if (expandedSectionId) {
      const element = elements.find(el => el.title === expandedSectionId);
      if (element) {
        onSelectionChangeRef.current({
          tab: activeTab,
          subTab: activeSubTab,
          element,
          parentContent: sub,
        });
      } else {
        // Fallback to the list if the element can't be found (e.g., content changed)
        onSelectionChangeRef.current({
          tab: activeTab,
          subTab: activeSubTab,
          elements,
          parentContent: sub,
        });
      }
    } else {
      onSelectionChangeRef.current({
        tab: activeTab,
        subTab: activeSubTab,
        elements,
        parentContent: sub,
      });
    }
    // IMPORTANT: we intentionally do NOT depend on `onSelectionChange`
  }, [activeTab, activeSubTab, expandedSectionId, content]);

  const handleParentTabChange = (tabKey: TabType) => {
    setActiveTab(tabKey);
    onTabChange?.(tabKey);
  };

  const handleSubTabChange = (subTabKey: string) => {
    setActiveSubTab(subTabKey);
    setExpandedSectionId(null);
  };

  const handleToggleSection = (id: string) => {
    setExpandedSectionId(prev => (prev === id ? null : id));
  };

  const activeSubContent = content[activeTab]?.[activeSubTab];

  return (
    <Tabs<TabType> className={styles.tabs} activeTab={activeTab} onTabChange={handleParentTabChange}>
      <motion.div className={styles.tabList} variants={tabContainerVariants} initial="hidden" animate="visible">
        {tabs.map(tab => (
          <motion.div key={tab.key} variants={tabItemVariants} style={{ flex: '1 1 clamp(100px, 20%, 200px)' }}>
            <Tab className={styles.tab} activeClassName={styles.activeTab} tabId={tab.key.toString()}>
              {tab.label}
            </Tab>
          </motion.div>
        ))}
      </motion.div>

      <Tabs className={styles.subTabs} activeTab={activeSubTab} onTabChange={handleSubTabChange}>
        <div className={styles.subTabList}>
          {Object.entries(content[activeTab] ?? {}).map(([subKey, subContent]) => (
            <div key={subKey}>
              <Tab className={styles.subTab} activeClassName={styles.activeSubTab} tabId={subKey}>
                <>
                  {CHART_TYPE_TO_SUBTAB_LABELS_MAPPING[chartType][subKey]}
                  {!subContent.is_unlocked && <LockIcon />}
                </>
              </Tab>
            </div>
          ))}
        </div>

        {/* @ts-expect-error framer-motion types incompatible with @types/react 18 */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${activeTab}-${activeSubTab}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className={styles.contentSection}
          >
            {activeSubContent?.description && (
              <motion.div
                className={styles.contentSectionDescription}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                {activeSubContent.description}
              </motion.div>
            )}

            {!activeSubContent?.is_unlocked ? (
              <PurchasePopUp onClick={() => onPurchaseClick?.()} />
            ) : (
              <motion.div variants={containerVariants} initial="hidden" animate="visible">
                {activeSubContent?.elements?.map(element => (
                  <motion.div
                    key={element.id ?? element.title}
                    variants={itemVariants}
                    className={styles.contentSectionElements}
                  >
                    <Button
                      variant="text"
                      className={styles.contentSectionElementsTitle}
                      onClick={() => handleToggleSection(element.title)}
                    >
                      {element.title}
                      <FiChevronDown
                        className={classNames(styles.chevronIcon, {
                          [styles.expanded]: expandedSectionId === element.title,
                        })}
                      />
                    </Button>
                    {/* @ts-expect-error framer-motion types incompatible with @types/react 18 */}
                    <AnimatePresence>
                      {expandedSectionId === element.title && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{
                            height: 'auto',
                            opacity: 1,
                            transition: {
                              height: { duration: 0.45, ease: [0.16, 1, 0.3, 1] },
                              opacity: { duration: 0.35, delay: 0.08 },
                            },
                          }}
                          exit={{
                            height: 0,
                            opacity: 0,
                            transition: {
                              opacity: { duration: 0.2 },
                              height: { duration: 0.35, ease: [0.16, 1, 0.3, 1], delay: 0.15 },
                            },
                          }}
                          style={{ overflow: 'hidden' }}
                        >
                          <motion.div
                            className={styles.contentSectionElementsDescription}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3, delay: 0.1 }}
                          >
                            <div>{element.description}</div>
                            {renderElementExtras?.({ element, parentContent: activeSubContent, expanded: true })}
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
      </Tabs>
    </Tabs>
  );
}

export default TabsContentPanel;
